import { Environment, HoppRESTRequest } from "@hoppscotch/data"
import { cloneDeep } from "lodash-es"
import {
  RuntimeSecretResolutionError,
  type RuntimeSecretsPlatformDef,
} from "~/platform/runtime-secrets"
import { getEffectiveRESTRequest } from "./utils/EffectiveURL"
import type { HoppRESTResponse } from "./types/HoppRESTResponse"

export { RuntimeSecretResolutionError } from "~/platform/runtime-secrets"

export type RuntimeSecretContext = {
  api: RuntimeSecretsPlatformDef
  teamID: string
  environmentID: string
  assertCurrent: () => void
  assertTransport?: () => void
}

// Only inspect fields used to send a request, not scripts, descriptions or files.
function visitStrings(value: unknown, visit: (value: string) => void): void {
  if (typeof value === "string") visit(value)
  else if (Array.isArray(value))
    value.forEach((item) => visitStrings(item, visit))
  else if (value && Object.getPrototypeOf(value) === Object.prototype) {
    const record = value as Record<string, unknown>
    if (record.active === false) return
    for (const [key, item] of Object.entries(record)) {
      if (key !== "description" && key !== "contentType")
        visitStrings(item, visit)
    }
  }
}

function requestStrings(
  request: HoppRESTRequest,
  visit: (value: string) => void
) {
  visitStrings(
    [
      request.endpoint,
      request.headers,
      request.params,
      request.body,
      request.auth.authActive ? request.auth : null,
    ],
    visit
  )
}

/** Resolve only aliases reachable from active outgoing fields, including nested variables. */
export function requiredRuntimeAliases(
  request: HoppRESTRequest,
  variables: Environment["variables"],
  approved: string[]
) {
  const required = new Set<string>()
  const visited = new Set<string>()
  const scan = (value: string) => {
    for (const [, alias] of value.matchAll(/<<([^<>]+)>>/g)) {
      if (alias.startsWith("$") || visited.has(alias)) continue
      visited.add(alias)
      if (approved.includes(alias)) required.add(alias)
      else {
        const variable = variables.find((item) => item.key === alias)
        // Unknown variables must fail closed rather than silently dropping a credential.
        if (!variable) required.add(alias)
        else scan(variable.currentValue ?? variable.initialValue ?? "")
      }
    }
  }
  requestStrings(request, scan)
  return [...required]
}

/** Values returned by this function belong only to the network execution, never stores. */
export async function prepareRuntimeRequest(
  request: HoppRESTRequest,
  environment: Environment,
  context: RuntimeSecretContext | null,
  signal: AbortSignal
) {
  if (!context)
    return { request: await getEffectiveRESTRequest(request, environment) }
  let hasVariables = false
  requestStrings(request, (value) => {
    if (/<<[^<>]+>>/.test(value)) hasVariables = true
  })
  if (!hasVariables)
    return { request: await getEffectiveRESTRequest(request, environment) }
  try {
    context.assertCurrent()
    if (signal.aborted)
      throw new RuntimeSecretResolutionError("Request cancelled.")
    const approved = context.environmentID
      ? await context.api.listAliases(
          context.teamID,
          context.environmentID,
          signal
        )
      : []
    const aliases = requiredRuntimeAliases(
      request,
      environment.variables,
      approved
    )
    if (!aliases.length)
      return { request: await getEffectiveRESTRequest(request, environment) }
    if (!context.environmentID)
      throw new RuntimeSecretResolutionError(
        "Select a team environment to retrieve runtime credentials."
      )
    context.assertTransport?.()
    context.assertCurrent()
    if (signal.aborted)
      throw new RuntimeSecretResolutionError("Request cancelled.")
    const values = await context.api.resolve(
      context.teamID,
      context.environmentID,
      aliases,
      signal
    )
    context.assertCurrent()
    if (signal.aborted)
      throw new RuntimeSecretResolutionError("Request cancelled.")
    if (
      aliases.some(
        (alias) =>
          !Object.hasOwn(values, alias) ||
          typeof values[alias] !== "string" ||
          !values[alias]
      )
    ) {
      throw new RuntimeSecretResolutionError(
        "A required runtime credential has no value."
      )
    }
    const executionEnv: Environment = {
      ...environment,
      variables: [
        ...aliases.map((key) => ({
          key,
          initialValue: values[key],
          currentValue: values[key],
          secret: true as const,
        })),
        ...environment.variables.filter((item) => !aliases.includes(item.key)),
      ],
    }
    const outgoing = await getEffectiveRESTRequest(
      cloneDeep(request),
      executionEnv
    )
    // Kernel auth is also derived from the auth object. Resolve its fields without
    // changing the original request used by scripts, history, or exports.
    const { parseTemplateString } = await import("@hoppscotch/data")
    const resolveAuth = (value: unknown): unknown => {
      if (typeof value === "string")
        return parseTemplateString(value, executionEnv.variables)
      if (value && typeof value === "object")
        return Object.fromEntries(
          Object.entries(value).map(([key, item]) => [key, resolveAuth(item)])
        )
      return value
    }
    outgoing.auth = resolveAuth(request.auth) as HoppRESTRequest["auth"]
    context.assertCurrent()
    context.assertTransport?.()
    return {
      request: outgoing,
      responseRequest: cloneDeep(request),
      sanitizeResponse: runtimeResponseSanitizer(
        values,
        outgoing.effectiveFinalHeaders
          .filter((h) => h.key.toLowerCase() === "authorization")
          .map((h) => h.value)
      ),
    }
  } catch (error) {
    // Only locally generated or the transport's static errors reach the UI.
    throw new RuntimeSecretResolutionError(
      error instanceof RuntimeSecretResolutionError
        ? error.message
        : "Could not prepare runtime credentials. Retrieval failed, timed out or was cancelled."
    )
  }
}

/** Also remove echoed credentials before response data can reach scripts or saved examples. */
export function runtimeResponseSanitizer(
  values: Record<string, string>,
  authHeaders: string[] = []
) {
  const replacements = Object.entries(values)
    .flatMap(([alias, value]) => [
      [value, `<<${alias}>>`],
      [encodeURIComponent(value), `<<${alias}>>`],
      [JSON.stringify(value).slice(1, -1), `<<${alias}>>`],
    ])
    .concat(
      authHeaders
        .filter(Boolean)
        .map((value) => [value, "[runtime credential]"])
    )
    .filter(([value]) => value.length > 0)
    .sort((a, b) => b[0].length - a[0].length)
  const redact = (value: string) =>
    replacements.reduce(
      (text, [secret, placeholder]) => text.split(secret).join(placeholder),
      value
    )
  return (response: HoppRESTResponse): HoppRESTResponse => {
    if (response.type === "success" || response.type === "failure") {
      // Replace matching UTF-8 byte sequences without corrupting binary responses.
      let bytes = new Uint8Array(response.body)
      for (const [secret, placeholder] of replacements) {
        const needle = new TextEncoder().encode(secret)
        const replacement = new TextEncoder().encode(placeholder)
        const parts: Uint8Array[] = []
        let start = 0
        for (let i = 0; i <= bytes.length - needle.length; i++) {
          if (needle.every((byte, offset) => bytes[i + offset] === byte)) {
            parts.push(bytes.slice(start, i), replacement)
            i += needle.length - 1
            start = i + 1
          }
        }
        if (parts.length) {
          parts.push(bytes.slice(start))
          const result = new Uint8Array(
            parts.reduce((size, part) => size + part.length, 0)
          )
          let offset = 0
          for (const part of parts) {
            result.set(part, offset)
            offset += part.length
          }
          bytes = result
        }
      }
      return {
        ...response,
        body: bytes.slice().buffer,
        headers: response.headers.map((h) => ({
          key: redact(h.key),
          value: redact(h.value),
        })),
        statusText: redact(response.statusText),
      }
    }
    if (response.type === "loading") return response
    // Interceptor errors can embed the entire outgoing request.
    return {
      type: "network_fail",
      req: "req" in response ? response.req : ({} as HoppRESTRequest),
      error: new Error("Request using runtime credentials failed."),
    }
  }
}
